import React, { useContext } from 'react';
import { useState } from 'react';
import { PdfTemplateContext } from '../../../../context/PdfTemplateContext';
import { useRequest } from '../../../../requests/common/requestHook';
import { PdfTemplateRequest } from '../../../../requests/pdf.request';
import { PdfTemplate, PdfTemplateEntitySubtype, PdfTemplateEntityType } from '../../../../types/pdf_template';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { translate } from '../../../../types/translation/Translator';
import { ExportUtil } from '../../../../util/export.util';
import { IconButton } from '../../../icons';
import { IconType } from '../../../icons/Icon';
import { Dropdown, DropdownList } from '../../Dropdown';
import { Button, ButtonVariant } from '../Button';

interface GeneratePdfButtonProps {
  entityId: string;
  entityType: PdfTemplateEntityType;
  entitySubtype: PdfTemplateEntitySubtype;
  type?: 'regular' | 'icon';
  text?: string;
  variant?: ButtonVariant;
}

export default function GeneratePdfButton({
  entityId,
  entityType,
  entitySubtype,
  type = 'regular',
  text,
  variant,
}: GeneratePdfButtonProps) {
  const { pdfTemplates } = useContext(PdfTemplateContext);

  const templates = [...pdfTemplates.values()];
  if (!templates.length) return null;

  const applicableTemplates = templates.filter(t => t.entityType === entityType && t.entitySubtype === entitySubtype);
  if (applicableTemplates.length === 0) return null;

  const [generate, { loading }] = useRequest(PdfTemplateRequest.generate);
  const [open, setOpen] = useState(false);

  const handleGenerate = async (template: PdfTemplate) => {
    const response = await generate({ templateId: template.id, entityId });
    if (!response) return;
    setOpen(false);
    ExportUtil.openInNewTab(response);
  };

  const handleClick = async (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (applicableTemplates.length === 1) {
      return await handleGenerate(applicableTemplates[0]);
    } else {
      setOpen(!open);
    }
  };

  text ||= translate({ key: TranslationKey.generatePdf });

  return (
    <Dropdown
      width='200px'
      open={open}
      setOpen={open => {
        if (!open) setOpen(false);
      }}
      dropdown={
        <DropdownList
          items={applicableTemplates}
          textBuilder={item => item.name}
          onClick={item => handleGenerate(item)}
        />
      }
    >
      {type === 'regular' ? (
        <Button text={text} variant={variant} loading={loading} onClick={handleClick} />
      ) : (
        <IconButton type={IconType.fileDownload02Icon} onClick={handleClick} text={text} />
      )}
    </Dropdown>
  );
}
